import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { CompanyAnalytics } from 'src/models';
import {
  fetchCompaniesPaymentsFlowAction,
  fetchPaymentsFlowAction,
  fetchPurchasesFlowAction,
} from 'src/services/analytics';

type Flow = 'payments' | 'companiesPayments' | 'purchasesByHour';

const analyticsEntityAdapter = createEntityAdapter({
  selectId: (record: CompanyAnalytics) => record.companyId,
});

export const analyticsSelectors = analyticsEntityAdapter.getSelectors();

const initialState: {
  pending: Record<Flow, boolean>;
  hasError: boolean;
} = {
  pending: {
    payments: false,
    purchasesByHour: false,
    companiesPayments: false,
  },
  hasError: false,
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState: analyticsEntityAdapter.getInitialState(initialState),
  reducers: {
    clearAnalyticsState: (state) => {
      state.ids = [];
      state.entities = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPaymentsFlowAction.pending, (state) => {
      state.pending.payments = true;
      state.hasError = false;
    });
    builder.addCase(
      fetchPaymentsFlowAction.fulfilled,
      (state, { meta, payload }) => {
        state.pending.payments = false;
        const company = analyticsSelectors.selectById(
          state,
          meta.arg.companyId,
        ) as CompanyAnalytics;
        analyticsEntityAdapter.upsertOne(state, {
          companyId: meta.arg.companyId,
          payments: payload,
          purchases: company?.purchases || [],
        });
      },
    );
    builder.addCase(fetchPaymentsFlowAction.rejected, (state) => {
      state.pending.payments = false;
      state.hasError = true;
    });

    builder.addCase(fetchCompaniesPaymentsFlowAction.pending, (state) => {
      state.pending.companiesPayments = true;
      state.hasError = false;
    });
    builder.addCase(
      fetchCompaniesPaymentsFlowAction.fulfilled,
      (state, { payload }) => {
        state.pending.companiesPayments = false;
        analyticsEntityAdapter.upsertMany(state, payload);
      },
    );
    builder.addCase(fetchCompaniesPaymentsFlowAction.rejected, (state) => {
      state.pending.companiesPayments = false;
      state.hasError = true;
    });

    builder.addCase(fetchPurchasesFlowAction.pending, (state) => {
      state.pending.purchasesByHour = true;
      state.hasError = false;
    });
    builder.addCase(
      fetchPurchasesFlowAction.fulfilled,
      (state, { meta, payload }) => {
        state.pending.purchasesByHour = false;
        const company = analyticsSelectors.selectById(
          state,
          meta.arg.companyId,
        ) as CompanyAnalytics;
        analyticsEntityAdapter.upsertOne(state, {
          companyId: meta.arg.companyId,
          payments: company?.payments || [],
          purchases: payload.statistics,
        });
      },
    );
    builder.addCase(fetchPurchasesFlowAction.rejected, (state) => {
      state.pending.purchasesByHour = false;
      state.hasError = true;
    });
  },
});

export const { clearAnalyticsState } = analyticsSlice.actions;

const { reducer } = analyticsSlice;

export default reducer;
